import {
	type Dtos,
	FinancialPeriodPreparationMethod,
	type ISODate,
	PeriodType,
	type WorkInProgressScheduleExtractionId,
} from "@inrev/common";
import { Option } from "@inrev/core";
import { DateTime } from "luxon";
import { formatEmpty } from "../../../../../utils/string";

const EMPTY_DISPLAY = formatEmpty(null);

const PERIOD_TYPE_LABEL_SEPARATOR = " · ";

export type WipAnalysisGridPeriodOrder = "newest-first" | "oldest-first";

/** Undated extractions sort last in either order. */
export const compareExtractions =
	(order: WipAnalysisGridPeriodOrder) =>
	(a: Dtos.Wip.Extraction.Get.Response, b: Dtos.Wip.Extraction.Get.Response): number => {
		const sign = order === "newest-first" ? -1 : 1;
		if (Option.isNone(a.statementDate) && Option.isNone(b.statementDate)) {
			return sign * a.createdAt.localeCompare(b.createdAt);
		}
		if (Option.isNone(a.statementDate)) return 1;
		if (Option.isNone(b.statementDate)) return -1;
		const byStatementDate = a.statementDate.localeCompare(b.statementDate);
		if (byStatementDate !== 0) return sign * byStatementDate;
		return sign * a.createdAt.localeCompare(b.createdAt);
	};

export const formatWipPeriodDateLabel = (date: Option<ISODate>): string =>
	Option.isNone(date) ? EMPTY_DISPLAY : DateTime.fromISO(date).toFormat("MM/dd/yyyy");

export const formatWipPeriodTypeLabel = (
	periodType: PeriodType | null,
	preparationMethod: FinancialPeriodPreparationMethod | null,
): string => {
	const parts = Option.filterNone([
		Option.map(periodType, PeriodType.displayName),
		Option.map(preparationMethod, FinancialPeriodPreparationMethod.displayName),
	]);
	return parts.length === 0 ? EMPTY_DISPLAY : parts.join(PERIOD_TYPE_LABEL_SEPARATOR);
};

export const scheduleOfRecordExtractionIds = (
	statements: ReadonlyArray<Dtos.Wip.Statement.Get.Response>,
): ReadonlySet<WorkInProgressScheduleExtractionId> =>
	new Set(Option.filterNone(statements.map((statement) => statement.activeExtractionId)));

export const latestScheduleOfRecordExtractionId = (
	statements: ReadonlyArray<Dtos.Wip.Statement.Get.Response>,
): Option<WorkInProgressScheduleExtractionId> => {
	const schedules = statements.flatMap((statement) => {
		const activeExtractionId = statement.activeExtractionId;
		if (Option.isNone(activeExtractionId)) return [];
		return [
			{
				activeExtractionId,
				statementDate: statement.statementDate,
				createdAt: statement.createdAt,
			},
		];
	});
	const latest = schedules.sort((a, b) => {
		const byDate = b.statementDate.localeCompare(a.statementDate);
		if (byDate !== 0) return byDate;
		return b.createdAt.localeCompare(a.createdAt);
	})[0];
	return latest?.activeExtractionId;
};
